/**
 * perf.ts — tiny main-thread timing ledger (the loading/feel round).
 *
 * `span(name, fn)` times a synchronous block and records it; `mark(name)`
 * stamps a point in time. Costs ~nothing (a Map write per call), ships in
 * production, and is how the boot / city-build / warm-up timings reach the
 * race diagnostics report (netdiag → `{t:'diag'}`) and the ?debug overlay.
 * With ?debug=1, `window.__perfSpans()` dumps the ledger for the perf probe
 * (scripts/perf-probe.mjs).
 *
 * Why a ledger and not console.time: the owner's phone reports arrive as
 * "it felt slow" — the ledger turns that into "buildScenery took 1.9 s on
 * that device", which is the only way a remote device can be diagnosed.
 */

const spans = new Map<string, { total: number; count: number; max: number; last: number }>();
const marks = new Map<string, number>();
const t0 = typeof performance !== 'undefined' ? performance.now() : 0;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function record(name: string, ms: number): void {
  const s = spans.get(name);
  if (s) {
    s.total += ms;
    s.count++;
    s.last = ms;
    if (ms > s.max) s.max = ms;
  } else {
    spans.set(name, { total: ms, count: 1, max: ms, last: ms });
  }
}

/** Time a synchronous block. Exceptions propagate; the span is still recorded. */
export function span<T>(name: string, fn: () => T): T {
  const start = now();
  try {
    return fn();
  } finally {
    record(name, now() - start);
  }
}

/** Record an externally-measured duration (async work timed by the caller). */
export function recordSpan(name: string, ms: number): void {
  record(name, ms);
}

/** Stamp a named moment (ms since module load — i.e. since the bundle ran). */
export function mark(name: string): void {
  marks.set(name, Math.round(now() - t0));
}

export function perfMark(name: string): number | undefined {
  return marks.get(name);
}

/** A compact, JSON-friendly dump: { spans: {name: [total, count, max]}, marks }. */
export function perfDump(): {
  spans: Record<string, [number, number, number]>;
  marks: Record<string, number>;
} {
  const out: Record<string, [number, number, number]> = {};
  for (const [k, v] of spans) out[k] = [Math.round(v.total), v.count, Math.round(v.max)];
  return { spans: out, marks: Object.fromEntries(marks) };
}

/** Debug handle — installed by main.ts under ?debug=1. */
export function exposePerf(): void {
  (window as unknown as { __perfSpans?: () => unknown }).__perfSpans = perfDump;
}
